import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Shield, Stethoscope } from 'lucide-react';
import { formatApiError, lastLocationApi } from '../lib/api';
import LanguageSwitcher from '../components/LanguageSwitcher';

const stats = [
  { value: '40%', label: 'Less Documentation Time', description: 'AI-powered notes reduce charting burden' },
  { value: '147%', label: 'Average ROI', description: 'Practices see returns within 3 years' },
  { value: '30%', label: 'Fewer Readmissions', description: 'Predictive risk scoring catches issues early' }
];

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentStat, setCurrentStat] = useState(0);
  const { login, completeMfaLogin, requestMfaEmailCode } = useAuth();
  const navigate = useNavigate();

  // MFA challenge state
  const [mfaStage, setMfaStage] = useState(null);  // null | 'challenge'
  const [mfaToken, setMfaToken] = useState('');
  const [mfaMethods, setMfaMethods] = useState([]);
  const [mfaMethod, setMfaMethod] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);

  // Rotate stats
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Fetch the user's last workspace and redirect there (if recent enough)
  // — otherwise fall back to /dashboard. Called post-login.
  const resumeOrDashboard = async () => {
    try {
      const { data } = await lastLocationApi.get();
      const path = data?.path;
      if (path && typeof path === 'string' && path !== '/' && !path.startsWith('/login')) {
        const updated = data.updated_at ? new Date(data.updated_at).getTime() : 0;
        // Resume if the last workspace was saved within 14 days — older than that
        // is probably stale and routing silently would be confusing.
        const fresh = Date.now() - updated < 14 * 24 * 60 * 60 * 1000;
        if (fresh) {
          toast.info(`Resuming: ${data.label || path}`);
          navigate(path);
          return;
        }
      }
    } catch { /* ignore — fall through to default */ }
    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await login(email, password);
      if (res?.requires_mfa) {
        setMfaToken(res.mfa_token);
        setMfaMethods(res.methods || []);
        setMfaMethod(res.primary_method || res.methods?.[0] || 'totp');
        setMfaStage('challenge');
        setLoading(false);
        return;
      }
      toast.success('Welcome back!');
      await resumeOrDashboard();
    } catch (error) {
      toast.error(formatApiError(error));
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await completeMfaLogin({ mfa_token: mfaToken, code: mfaCode, method: mfaMethod, remember_device: rememberDevice });
      toast.success('Welcome back!');
      await resumeOrDashboard();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setLoading(false); }
  };

  const switchToEmailOtp = async () => {
    try {
      const r = await requestMfaEmailCode(mfaToken);
      setMfaMethod('email');
      setMfaCode('');
      toast.success(`Code sent to ${r.sent_to}`);
    } catch (error) { toast.error(formatApiError(error)); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Floating language switcher — visible on every breakpoint */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher variant="auth" testId="login-language-switcher" />
      </div>
      {/* Left Panel - Navy with stats */}
      <div className="hidden lg:flex lg:w-[40%] login-split-bg relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/b4e0bfa7-d675-416a-864e-57470211af20/images/2731f93f1af3a34838319c2533b72564082b595d8ed6425ad3a3e8fe2d7b6e65.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <Link to="/" className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                <Stethoscope className="w-7 h-7 text-white" />
              </div>
              <span className="text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Savenes
              </span>
            </Link>
            <p className="text-[#94A3B8] mt-4 text-lg">
              Smarter care. Healthier outcomes.
            </p>
          </div>

          {/* Rotating Stats */}
          <div className="py-12">
            <div className="transition-all duration-500">
              <div className="text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {stats[currentStat].value}
              </div>
              <div className="text-2xl text-white font-medium mb-2">
                {stats[currentStat].label}
              </div>
              <div className="text-[#94A3B8] text-lg">
                {stats[currentStat].description}
              </div>
            </div>
            
            {/* Stat indicators */}
            <div className="flex gap-2 mt-8">
              {stats.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === currentStat ? 'w-8 bg-[#0E7490]' : 'w-4 bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-[#94A3B8]">
            <Shield className="w-5 h-5 text-[#0E7490]" />
            <span>HIPAA Compliant • SOC 2 Type II Certified</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-lg bg-[#0E7490] flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Savenes
            </span>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-semibold text-[#0A2342] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {mfaStage === 'challenge' ? 'Verify your identity' : t('auth.welcomeBack')}
            </h1>
            <p className="text-[#64748B]">
              {mfaStage === 'challenge' ? (mfaMethod === 'email' ? 'Enter the 6-digit code sent to your email' : mfaMethod === 'recovery' ? 'Enter one of your recovery codes' : 'Enter the 6-digit code from your authenticator app') : t('auth.signInSubtitle')}
            </p>
          </div>

          {mfaStage === 'challenge' ? (
            <form onSubmit={handleMfaSubmit} className="space-y-6" data-testid="mfa-challenge-form">
              <div>
                <Label htmlFor="mfaCode" className="careiq-label">{mfaMethod === 'recovery' ? 'Recovery code' : 'Verification code'}</Label>
                <Input
                  id="mfaCode"
                  type="text"
                  autoComplete="one-time-code"
                  placeholder={mfaMethod === 'recovery' ? 'XXXX-XXXX-XXXX' : '000000'}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="careiq-input text-center text-2xl tracking-widest font-mono"
                  required
                  autoFocus
                  data-testid="mfa-code-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full careiq-btn-primary py-6 text-lg flex items-center justify-center gap-2"
                disabled={loading}
                data-testid="mfa-verify-button"
              >
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>Verify<ArrowRight className="w-5 h-5" /></>}
              </Button>

              <label className="flex items-center gap-2 text-sm text-[#64748B] cursor-pointer">
                <input type="checkbox" checked={rememberDevice} onChange={e => setRememberDevice(e.target.checked)} className="rounded" data-testid="remember-device-checkbox" />
                Remember this device for 30 days
              </label>

              <div className="text-center text-xs text-[#64748B] flex flex-wrap gap-2 justify-center">
                {mfaMethods.includes('email') && mfaMethod !== 'email' && (
                  <button type="button" onClick={switchToEmailOtp} className="text-[#0E7490] hover:underline" data-testid="mfa-email-option">Use email code</button>
                )}
                {mfaMethods.includes('totp') && mfaMethod !== 'totp' && (
                  <button type="button" onClick={() => { setMfaMethod('totp'); setMfaCode(''); }} className="text-[#0E7490] hover:underline" data-testid="mfa-totp-option">Use authenticator app</button>
                )}
                {mfaMethod !== 'recovery' && (
                  <button type="button" onClick={() => { setMfaMethod('recovery'); setMfaCode(''); }} className="text-[#0E7490] hover:underline" data-testid="mfa-recovery-option">Use recovery code</button>
                )}
                <button type="button" onClick={() => { setMfaStage(null); setMfaCode(''); setPassword(''); }} className="text-[#64748B] hover:underline" data-testid="mfa-cancel">Cancel</button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="careiq-label">
                {t('auth.emailAddress')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="careiq-input pl-10"
                  required
                  autoFocus
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password" className="careiq-label mb-0">
                  {t('common.password')}
                </Label>
                <button
                  type="button"
                  className="text-sm text-[#0E7490] hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="careiq-input pl-10 pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B]"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                className="w-4 h-4 rounded border-[#E2E8F0] text-[#0E7490] focus:ring-[#0E7490]"
                data-testid="remember-checkbox"
              />
              <label htmlFor="remember" className="text-sm text-[#64748B]">
                {t('auth.rememberDevice')}
              </label>
            </div>

            <Button
              type="submit"
              className="w-full careiq-btn-primary py-6 text-lg flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {t('auth.signIn')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </form>
          )}

          {/* HIPAA Notice */}
          <div className="mt-8 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
            <p className="text-xs text-[#64748B] leading-relaxed">
              <Shield className="w-4 h-4 inline-block mr-1 text-[#0E7490]" />
              {t('auth.hipaaNotice')}{' '}
              <a href="#" className="text-[#0E7490] hover:underline">{t('auth.privacyPolicy')}</a>
            </p>
          </div>

          <p className="text-center text-sm text-[#64748B] mt-8">
            {t('auth.noAccount')}{' '}
            <Link to="/request-access" className="text-[#0E7490] font-medium hover:underline" data-testid="login-request-access-link">
              {t('auth.requestAccess')}
            </Link>
          </p>

          {/* Recovery: clear all local state + service worker (for users stuck in a loop) */}
          <p className="text-center text-[10px] text-[#94A3B8] mt-4">
            <button
              type="button"
              onClick={async () => {
                try {
                  localStorage.clear();
                  sessionStorage.clear();
                  if ('serviceWorker' in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    await Promise.all(regs.map((r) => r.unregister()));
                  }
                  if (window.caches) {
                    const keys = await caches.keys();
                    await Promise.all(keys.map((k) => caches.delete(k)));
                  }
                } finally {
                  window.location.href = '/login';
                }
              }}
              className="underline hover:text-[#64748B]"
              data-testid="clear-app-state-btn"
            >
              Trouble signing in? Reset app state
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
